import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { 
  FaCheckCircle, FaBuilding, FaWeightHanging, FaMapMarkerAlt,
  FaMoneyBillWave, FaEye, FaArrowRight, FaCalendarAlt,
  FaTruck, FaClock, FaFileInvoice
} from 'react-icons/fa';
import SubbieLayout from '../layouts/SubbieLayout';

function SubbieClosedJobs({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState({ loads: [], requests: [] });
  const [subbieId, setSubbieId] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    const getSubbieId = async () => {
      try {
        let savedId = localStorage.getItem('subbieId');
        if (savedId) {
          setSubbieId(savedId);
          fetchData(savedId);
          return;
        }

        const response = await fetch(`/subcontractors?user_id=${user.id}`);
        const data = await response.json();
        if (data.success && data.subcontractors && data.subcontractors.length > 0) {
          const id = data.subcontractors[0].id;
          setSubbieId(id);
          localStorage.setItem('subbieId', id);
          fetchData(id);
        } else {
          setError('No subcontractor profile found');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching subbie ID:', err);
        setError('Failed to load profile');
        setLoading(false);
      }
    };

    if (user.id) {
      getSubbieId();
    } else {
      setError('User not logged in');
      setLoading(false);
    }
  }, []);

  const fetchData = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/subbie/history/${id}`);
      const data = await response.json();
      if (data.success) {
        setHistory(data);
      } else {
        setError(data.message || 'Failed to load history');
      }
    } catch (err) {
      console.error('Error fetching history:', err);
      setError('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <SubbieLayout activeTab="closedjobs" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4">🔄</div>
            <p className="text-gray-500">Loading history...</p>
          </div>
        </div>
      </SubbieLayout>
    );
  }

  if (error) {
    return (
      <SubbieLayout activeTab="closedjobs" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border border-gray-200 max-w-md mx-auto">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading History</h2>
          <p className="text-gray-500">{error}</p>
          <button 
            onClick={() => fetchData(subbieId)}
            className="mt-4 bg-navy text-white px-6 py-2 rounded-lg hover:bg-green hover:text-navy transition-colors"
          >
            Retry
          </button>
        </div>
      </SubbieLayout>
    );
  }

  const allCompleted = [...(history.loads || []), ...(history.requests || [])];
  const totalJobs = allCompleted.length;
  const totalTonnage = history.loads?.reduce((sum, l) => sum + (l.weight_mt || 0), 0) || 0;

  return (
    <SubbieLayout activeTab="closedjobs" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">✅ Closed Jobs</h1>
          <p className="text-gray-500 text-sm">All completed jobs</p>
        </div>
        <span className="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
          {totalJobs} completed
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600">
              <FaCheckCircle size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold text-navy">{totalJobs}</div>
              <div className="text-xs text-gray-500">Total Jobs</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
              <FaWeightHanging size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold text-navy">{totalTonnage.toFixed(0)} MT</div>
              <div className="text-xs text-gray-500">Total Tonnage</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center text-purple-600">
              <FaMoneyBillWave size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold text-navy">
                R{history.requests?.reduce((sum, r) => sum + (r.final_rate * r.jobs?.total_quantity_mt || 0), 0).toLocaleString() || 0}
              </div>
              <div className="text-xs text-gray-500">Estimated Revenue</div>
            </div>
          </div>
        </div>
      </div>

      {totalJobs === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-xl font-semibold text-navy mb-2">No Closed Jobs Yet</h3>
          <p className="text-gray-500">Completed jobs will appear here once you finish them.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {allCompleted.map((item, index) => {
            const job = item.jobs || item;
            const isLoad = item.load_number !== undefined;
            return (
              <div key={index} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-navy">{job?.job_number || item.load_number || 'Unknown'}</h3>
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-600">
                        ✅ COMPLETED
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      <FaBuilding className="inline mr-1 text-green" size={14} />
                      Client details withheld
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-2">
                      {job?.commodity && (
                        <span className="flex items-center gap-1">
                          <FaTruck size={14} /> {job.commodity}
                        </span>
                      )}
                      {job?.total_quantity_mt && (
                        <span className="flex items-center gap-1">
                          <FaWeightHanging size={14} /> {job.total_quantity_mt} MT
                        </span>
                      )}
                      {job?.origin && job?.destination && (
                        <span className="flex items-center gap-1">
                          <FaMapMarkerAlt size={14} /> {job.origin} → {job.destination}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <FaCalendarAlt size={14} /> {job?.start_date || 'N/A'}
                      </span>
                    </div>
                    {isLoad && (
                      <div className="mt-2 p-2 bg-gray-50 rounded-lg text-sm text-gray-600">
                        <FaTruck className="inline mr-1" /> Load completed
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:border-green hover:text-green transition-colors">
                      <FaEye size={14} /> View
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:border-blue-500 hover:text-blue-500 transition-colors">
                      <FaFileInvoice size={14} /> Invoice
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SubbieLayout>
  );
}

export default SubbieClosedJobs;
